import { useLazyQuery } from '@apollo/client/react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { GetInvitationCodesDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { invitationHistoryPageInfoState, invitationModalState } from '~/state/reception/atoms';

export const useInvitationHistory = () => {
  const [state, setState] = useAtom(invitationHistoryPageInfoState);
  const { isOpen } = useAtomValue(invitationModalState);
  const [getInvitationCodes, { loading, data }] = useLazyQuery(GetInvitationCodesDocument, {
    fetchPolicy: 'network-only',
  });
  const invitationCodes = useMemo(
    () => (data ? getMe(data)?.organization.invitationCodes.nodes || [] : []),
    [data],
  );

  useEffect(() => {
    if (!data) return;
    setState((state) => ({
      ...state,
      totalPage: getMe(data)?.organization.invitationCodes.pagesCount || 1,
    }));
  }, [data, setState]);

  const handleChangePage = useCallback(
    (page: number) =>
      setState((_state) => ({
        ..._state,
        page,
      })),
    [setState],
  );
  const handleSendFailedFilter = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setState((_state) => ({
        ..._state,
        page: 1,
        isSendFailed: e.target.checked,
      })),
    [setState],
  );

  const handleNotAppliedFilter = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setState((_state) => ({
        ..._state,
        page: 1,
        isNotApplied: e.target.checked,
      })),
    [setState],
  );

  useEffect(() => {
    if (isOpen) {
      getInvitationCodes({
        variables: {
          sendFailed: state.isSendFailed,
          notApplied: state.isNotApplied,
          page: state.page,
          perPage: state.perPage,
        },
      });
    }
  }, [
    getInvitationCodes,
    state.isSendFailed,
    state.isNotApplied,
    state.page,
    state.perPage,
    isOpen,
  ]);

  return {
    loading,
    state,
    invitationCodes,
    handleChangePage,
    handleSendFailedFilter,
    handleNotAppliedFilter,
  };
};
